import { COVERED, allFunds, holdingsOf } from '~data';

/**
 * What a fund actually does: whether it writes calls, and whether it borrows.
 *
 * Unlike sectors.ts and countries.ts beside it, nothing here is authored. Both
 * facts are read out of the issuer's own published holdings, which is why this
 * is a derivation and not a list — a fund launched next quarter classifies
 * itself the moment its holdings land in the artefact, where a hand-kept table
 * would quietly go stale and start lying to the screener.
 *
 * The two facts are orthogonal and are kept that way. "Leveraged covered call"
 * is not a third thing, it is both flags true, and treating it as its own
 * category is how you end up with a fund that is somehow neither.
 */

export interface Strategy {
    /** Writes call options — directly, or through the funds it holds. */
    calls: boolean;
    /** Holds more than a dollar of assets per dollar of units. */
    leverage: boolean;
}

/**
 * The two lines that are not positions.
 *
 * Every issuer publishes a residual cash line and, where it writes them, an
 * options line. Both have to come out before the rest can be summed against
 * 100%, and the two issuers disagree on sign — Hamilton posts borrowed cash
 * negative and normalises the total to 100, Harvest posts it positive and lets
 * the total run to 148 — so neither the sign nor the total is usable directly.
 * What survives both conventions is the sum of the actual positions.
 */
const isOptions = (name: string): boolean => /written\s+(call\s+|put\s+)?option/i.test(name);
const isCash = (name: string): boolean => /^cash and other assets/i.test(name.trim());

/** Sum of the real positions, as a percentage of net assets. */
const invested = (symbol: string): number =>
    holdingsOf(symbol)
        .filter((h) => !isOptions(h.name ?? '') && !isCash(h.name ?? ''))
        .reduce((sum, h) => sum + (h.weight ?? 0), 0);

/**
 * Above this much invested, the fund is borrowing.
 *
 * The floor is not 100. Weights are published rounded to a tenth and a fund
 * with thirty holdings can add to 101.8 with no leverage at all — Harvest's
 * unlevered funds land between 99 and 102. The levered ones are nowhere near
 * that band: the closest is 115. Anything between 105 and 115 would be a fund
 * whose disclosure has changed shape, and it should be looked at rather than
 * silently bucketed, so the gap is left wide rather than split down the middle.
 */
const LEVERAGE_FLOOR = 105;

/** Enough of a fund-of-funds' constituents write calls that the wrapper does. */
const MAJORITY = 50;

/**
 * Symbol for a holding, including the ones published by name only.
 *
 * Hamilton tags its fund-of-funds holdings with tickers; Harvest does not, so
 * HDIF's constituents arrive as "Harvest Tech Leaders Income ETF" and nothing
 * else. Matched back by name, which is exact for every case here because both
 * strings come from the same issuer.
 */
const byName = new Map<string, string>();
for (const f of allFunds()) if (f.name != null) byName.set(f.name.toLowerCase(), f.symbol);

const symbolFor = (holdingSymbol: string | null, holdingName: string | null): string | null =>
    holdingSymbol ?? (holdingName == null ? null : (byName.get(holdingName.toLowerCase()) ?? null));

/**
 * Whether this fund writes calls, looking through the funds it holds.
 *
 * A wrapper writes no options of its own — the calls are written a level down,
 * inside the funds it owns — so the disclosure that proves it is not on its own
 * holdings page. `seen` guards the cycle; a fund reached twice contributes
 * nothing rather than recursing forever.
 */
const writesCalls = (symbol: string, seen: ReadonlySet<string> = new Set()): boolean => {
    const holdings = holdingsOf(symbol);
    if (holdings.some((h) => isOptions(h.name ?? ''))) return true;

    const positions = holdings.filter((h) => !isOptions(h.name ?? '') && !isCash(h.name ?? '') && (h.weight ?? 0) > 0);
    const total = positions.reduce((sum, h) => sum + (h.weight ?? 0), 0);
    if (total <= 0) return false;

    let writing = 0;
    for (const h of positions) {
        const inner = symbolFor(h.symbol, h.name);
        // Measured against every position, not just the ones that resolved: a
        // wrapper whose constituents are mostly unknown to us has not been
        // shown to write calls, and should not claim to.
        if (inner == null || seen.has(inner)) continue;
        if (writesCalls(inner, new Set([...seen, symbol]))) writing += h.weight ?? 0;
    }
    return (writing / total) * 100 >= MAJORITY;
};

const cache = new Map<string, Strategy>();

/** What this fund does, or nulls where it publishes nothing to judge on. */
export const strategyOf = (symbol: string): Strategy => {
    const hit = cache.get(symbol);
    if (hit) return hit;
    const strategy: Strategy = { calls: writesCalls(symbol), leverage: invested(symbol) > LEVERAGE_FLOOR };
    cache.set(symbol, strategy);
    return strategy;
};

/**
 * The presets on the screener, in the order they are shown.
 *
 * Deliberately overlapping rather than a partition: every levered fund in the
 * pool today also writes calls, so a mutually exclusive "covered call" would
 * have to mean "covered call and not levered" — a definition nobody holds, and
 * one that hides HDIV from the chip named after exactly what HDIV is.
 */
export type PresetId = 'calls' | 'leverage' | 'both';

export const PRESETS: { id: PresetId; label: string; match: (s: Strategy) => boolean }[] = [
    { id: 'calls', label: 'Covered call', match: (s) => s.calls },
    { id: 'leverage', label: 'Leveraged', match: (s) => s.leverage },
    { id: 'both', label: 'Leveraged covered call', match: (s) => s.calls && s.leverage },
];

export const matchesPreset = (symbol: string, preset: PresetId): boolean => {
    const spec = PRESETS.find((p) => p.id === preset);
    return spec == null ? true : spec.match(strategyOf(symbol));
};

/** How many of the pool each chip would leave, for the count beside its label. */
export const presetCount = (preset: PresetId): number => COVERED.filter((f) => matchesPreset(f.symbol, preset)).length;
